<template>
  <div v-if="$screen.isDesktop($mq)" class="bulk-edit-panel">
    <div class="bulk-edit-panel__main-control">
      <dd-icon-button
        class="close-btn" 
        icon="close" 
        size="small" 
        type="circle" 
        @click="$emit('edit-canceled')"
      />
      <loading-placeholder v-if="isLoadingOptions" :lines="1" />
      <template v-else>
        <div class="bulk-edit-panel__main-control--title">
          <i class="ddi ddi-edit" />
          {{ itemCountLabel }}
        </div>
        <div class="bulk-edit-panel__main-control--insta-clicks">
          <!-- DELETE -->
          <dd-button
            v-if="$permissions.isAdminOrOwner"
            :is-loading="isDeleting"
            size="tiny"
            type="flat"
            color="strawberry"
            @click="handleDeleteOptionSelected()"
          >
            <i class="ddi ddi-trash" />&nbsp;<span>Delete</span>
          </dd-button>
          <!-- SPAM -->
          <dd-button
            v-if="showSpamButton"
            :is-loading="isSpamUpdating && $permissions.isNotGuest"
            size="tiny"
            type="flat"
            color="bluish-grey"
            @click="handleSpamOptionSelected()"
          >
            <i class="ddi ddi-spam" />&nbsp;<span>Spam</span></dd-button>
          <!-- REMOVE FROM SPAM -->
          <dd-button
            v-if="showUnSpamButton"
            :is-loading="isSpamUpdating && $permissions.isNotGuest"
            size="tiny"
            type="flat"
            color="bluish-grey"
            @click="handleUnSpamOptionSelected()"
          >
            <i class="ddi ddi-spam" />&nbsp;<span>Not Spam</span></dd-button>
          <!-- WATCH... -->
          <dd-button
            :is-loading="isWatchUpdating" 
            size="tiny"
            type="flat"
            @click="handleWatchOptionSelected()"
          >
            <i class="ddi ddi-watching" />&nbsp;<span>Watch</span></dd-button> 
          <!-- MOVE... -->
          <dd-button
            v-if="showMoveButton && $permissions.isNotGuest"
            size="tiny"
            type="flat"
            @click="$emit('move-items-clicked')"
          >
            <i class="ddi ddi-move-up" />&nbsp;<span>Move&hellip;</span></dd-button> 
          <!-- MERGE... -->
          <dd-button
            v-if="showMergeButton && $permissions.isNotGuest"
            size="tiny"
            type="flat"
            @click="$emit('merge-convos-clicked')"
          >
            <i class="ddi ddi-merge-down rotate-270" />&nbsp;<span>Merge&hellip;</span></dd-button> 
        </div>
        <div class="bulk-edit-panel__main-control--update-options">
          <!-- Assignee, if no jobs -->
          <dropdown v-if="allSelectedEntitiesUseAssignees && $permissions.isNotGuest" title="Assignee">
            <bep-options-tab label="Assignee" />
            <template #content>
              <div>
                <ul>
                  <li v-for="assignee in curatedAssigneeOptions" :key="assignee.id" @click="handleAssigneeSelected(assignee.id)">
                    <avatar :person="assignee" :size="20"/>
                    <span :class="{ 'selected' : selectedPersonID === assignee.id }">
                      {{ assignee.name }}
                      <i v-if="assignee.isGuest" class="ddi ddi-warning" title="Guest user" />
                    </span>
                  </li>
                </ul>
              </div>
            </template>
          </dropdown>
          <!-- Jobs, if available  -->
          <dropdown v-if="hasJobOptions && $permissions.isNotGuest" class="jobs-dropdown">
            <bep-options-tab label="Jobs..." />
            <template #content>
              <div>
                <ul>
                  <li 
                    v-for="job in jobOptions" 
                    :key="job.id" 
                    @click="selectedJobName=job.name"
                  >
                    <!-- Beside the job: -8 lines the first appointee up with it, 14 clears the jobs list by 1px -->
                    <dropdown :key="job.id" :title="job.name" placement="right-start" :offset="[-8, 14]">
                      <div :class="{ 'selected' : selectedJobName === job.name }" class="jobs-dropdown__label">
                        <span>Change <strong>{{ job.name }}</strong> to&hellip;</span>
                        <i v-if="selectedJobName === job.name" class="ddi ddi-arrow-right" />
                      </div>
                      <template #content>
                        <div>
                          <ul>
                            <li v-for="appointee in curatedAssigneeOptions" :key="appointee.id" @click="handleJobAppointeeSelected(job.name, appointee.id)">
                              <avatar :person="appointee" :size="20"/>
                              <span :class="{ 'selected' : selectedPersonID === appointee.id }">{{ appointee.name }}</span>
                            </li>
                          </ul>
                        </div>
                      </template>
                    </dropdown>
                  </li>
                </ul>
              </div>
            </template>
          </dropdown>
          <!-- Statuses -->
          <dropdown v-if="$permissions.isNotGuest" :is-disabled="!hasStatusOptions" title="Status" >
            <bep-options-tab :is-disabled="!hasStatusOptions" label="Status" />
            <template #content>
              <div>
                <ul>
                  <li v-for="status in statusOptions" :key="status.id" @click="handleStatusSelected(status.name)">
                    <span :class="{ 'selected' : selectedStatusName === status.name }">
                      <status-chicklet :status="status" />
                    </span>
                  </li>
                </ul>
              </div>
            </template>
          </dropdown>
          <!-- Priority -->
          <dropdown v-if="$permissions.isNotGuest" title="Priority">
            <bep-options-tab label="Priority" />
            <template #content>
              <div>
                <ul>
                  <li v-for="priority in priorityOptions" :key="priority.id" @click="handlePrioritySelected(priority.id)">
                    <span :class="{ 'selected' : selectedPriorityID === priority.id }">
                      <priority-chicklet :priority="priority" />
                    </span>
                  </li>
                </ul>
              </div>
            </template>
          </dropdown>
          <!-- Due -->
          <due-date-picker
            v-if="$permissions.isNotGuest"
            ref="datePicker"
            v-model="selectedDueDate"
            emit-event-on-clear
            @clear-due-date-clicked="removeDueDate()"
          >
            <template #button>
              <bep-options-tab label="Due" />
            </template>
          </due-date-picker>
          <!-- Tags -->
          <dropdown v-if="$permissions.isNotGuest" title="Tags" placement="bottom-end" class="tags-dropdown">
            <bep-options-tab label="Tags" />
            <template #content>
              <div class="tags-dropdown__content">
                <tags-input
                  v-model:selected-tags="selectedTags"
                  :available-tags="tagOptions"
                  hide-icon
                  enable-dropdown-absolute-positioning
                />
                <div class="buttons">
                  <dd-button :is-loading="isTagsUpdating" :is-disabled="!hasSelectedTags" size="tiny" type="flat" @click="addTags()">Add {{tagsLabel}}</dd-button>
                  <dd-button :is-loading="isTagsUpdating" :is-disabled="!hasSelectedTags" size="tiny" type="flat" color="red" @click="removeTags()">Remove {{tagsLabel}}</dd-button>
                  <dd-button :is-loading="isTagsUpdating" :is-disabled="!hasSelectedTags" size="tiny" type="text" @click="clearSelectedTags()">Clear</dd-button>
                </div>
              </div>
            </template>
          </dropdown>
          <!-- Comment -->
          <bep-options-tab 
            label="Comment" 
            use-add-button 
            @click="$emit('add-comment-clicked')"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { isEqual } from 'lodash-es'

/* Mixins */

/* Components */
import BepOptionsTab from './bep-options-tab.vue'

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'
import AssigneeAssistant from '@/helpers/assignee-assistant'

/* Data types */
import { EEntities } from '@/types'
import {
  IItemForSelection,
  IPersonWithEmail,
  IPriorityLevelForSelection,
  IProjectForSelection,
  IStatusForSelection,
} from '@/api/types/response.types'

/* API */
import TasksAPI from '@/api/modules/tasks.api'
import ConversationsAPI from '@/api/modules/convos.api'
import {
  IConvosInputBulkEditAssignee,
  IConvosInputBulkEditDueDate,
  IConvosInputBulkEditJobAppointee,
  IConvosInputBulkEditPriorityLevel,
  IConvosInputBulkEditStatus,
  IConvosInputBulkEditTags,
  ITasksInputBulkEditAssignee,
  ITasksInputBulkEditDueDate,
  ITasksInputBulkEditJobAppointee,
  ITasksInputBulkEditPriorityLevel,
  ITasksInputBulkEditStatus,
  ITasksInputBulkEditTags,
} from '@/api/types/request.types'

export default defineComponent({
  name: 'BulkEditPanel',
  components: { BepOptionsTab },
  props: {
    selectedIds: {
      type: Array as () => number[],
      default: (): number[] => [],
    },
    selectedEntityIds: {
      type: Array as () => number[],
      default: (): number[] => [],
    },
    entityType: {
      type: String as () => EEntities,
      required: true,
    },
    isSpamFilterSelected: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'add-comment-clicked',
    'edit-canceled',
    'edit-completed',
    'merge-convos-clicked',
    'move-items-clicked',
  ],

  data() {
    return {
      TextAssistant,
      isLoadingOptions: true,

      isSubmitting: false,
      isSpamUpdating: false,
      isWatchUpdating: false,
      isDeleting: false,
      isTagsUpdating: false,

      entityOptions: [] as IProjectForSelection[],
      priorityOptions: [] as IPriorityLevelForSelection[],
      assigneeOptions: [] as IPersonWithEmail[],
      tagOptions: [] as string[],
      statusOptions: [] as IStatusForSelection[],
      jobOptions: [] as IItemForSelection[],
      allSelectedEntitiesUseAssignees: false,

      selectedPriorityID: null as any,
      // Used for both assignee and job(s) dropdowns. Since this is just used to temporarily show
      // selected state of dropdown while the bulk edit is being made, its fine to reuse it
      // across multiple dropdowns (it just means that person is selected within each dropdown
      // during the duration of the update.)
      selectedPersonID: null as any,
      selectedStatusName: null as any,
      selectedJobName: null as any,
      selectedDueDate: null as any,
      selectedTags: [] as string[],
      removeSelectedTags: false,

      comment: null as any,
    }
  },

  computed: {
    hasStatusOptions(): boolean {
      return this.statusOptions !== null && this.statusOptions.length > 0
    },
    hasJobOptions(): boolean {
      return this.jobOptions !== null && this.jobOptions.length > 0
    },
    curatedAssigneeOptions(): IPersonWithEmail[] {
      return AssigneeAssistant.getFullAssigneeOptions(
        this.assigneeOptions,
        this.$permissions,
      )
    },
    showMergeButton(): boolean {
      return (
        this.isConvoRelated &&
        this.selectedEntityIds.length === 1 &&
        this.selectedIds.length > 1 &&
        this.selectedIds.length <= 10
      )
    },
    showMoveButton(): boolean {
      return this.selectedEntityIds.length === 1
    },
    showSpamButton(): boolean {
      return this.isConvoRelated && !this.isSpamFilterSelected
    },
    showUnSpamButton(): boolean {
      return this.isConvoRelated && this.isSpamFilterSelected
    },
    isConvoRelated(): boolean {
      return this.entityType === EEntities.MAILBOXES
    },
    selectedRowsCount(): number {
      return this.selectedIds.length
    },
    hasSelectedTags(): boolean {
      return this.selectedTags.length > 0
    },
    tagsLabel(): string {
      return this.selectedTags.length === 1 ? 'tag' : 'tags'
    },
    itemCountLabel(): string {
      if (this.selectedRowsCount === 1) {
        return `One ${TextAssistant.itemSingularName(
          this.entityType,
        ).toLowerCase()}`
      } else {
        return `${this.selectedRowsCount} ${TextAssistant.itemPluralName(
          this.entityType,
        ).toLowerCase()}`
      }
    },
  },

  watch: {
    selectedEntityIds: {
      deep: 1,
      immediate: true,
      async handler(newVal, oldVal) {
        if (!isEqual(oldVal, newVal)) {
          await this.fetchOptions()
        }
      },
    },
    selectedDueDate: {
      handler(val) {
        // Null (or 'clear due date') handling explicitly using removeDueDate()
        if (val !== null) {
          this.updateDueDate()
        }
      },
    },
  },

  methods: {
    handleSuccess(successMessage: string) {
      this.$messaging.showSuccess(successMessage)
      this.$emit('edit-completed')
    },
    async handleSpamOptionSelected() {
      if (this.isSubmitting) {
        return
      }
      this.isSubmitting = true
      this.isSpamUpdating = true
      try {
        const response = await ConversationsAPI.put_BulkEdit_Spam(
          this.selectedIds,
        )
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.isSpamUpdating = false
      }
    },
    async handleUnSpamOptionSelected() {
      if (this.isSubmitting) {
        return
      }

      this.isSubmitting = true
      this.isSpamUpdating = true

      try {
        const response = await ConversationsAPI.put_BulkEdit_UnSpam(
          this.selectedIds,
        )
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.isSpamUpdating = false
      }
    },
    async handleWatchOptionSelected() {
      if (this.isSubmitting) {
        return
      }

      this.isSubmitting = true
      this.isWatchUpdating = true

      try {
        let response
        if (this.isConvoRelated) {
          response = await ConversationsAPI.put_BulkEdit_Watch(this.selectedIds)
        } else {
          response = await TasksAPI.put_BulkEdit_Watch(this.selectedIds)
        }
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.isWatchUpdating = false
      }
    },
    async handleDeleteOptionSelected() {
      if (this.isSubmitting) {
        return
      }

      await this.$confirm(
        `Are you sure you want to delete ${this.itemCountLabel.toLowerCase()}?<br>There is no undo.`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'Yes, Delete',
          confirmButtonClass: 'el-button--strawberry',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'el-button--text',
          type: 'warning',
          title: `Delete ${this.itemCountLabel.toLowerCase()}`,
        },
      )
        .then(async () => {
          let response

          this.isSubmitting = true
          this.isDeleting = true
          if (this.isConvoRelated) {
            response = await ConversationsAPI.put_BulkEdit_Delete(
              this.selectedIds,
            )
          } else {
            response = await TasksAPI.put_BulkEdit_Delete(this.selectedIds)
          }
          this.handleSuccess(response.message)
          this.isSubmitting = false
          this.isDeleting = false
        })
        .catch(error => {
          this.$error.emit(error, true)
          this.isSubmitting = false
          this.isDeleting = false
        })
    },

    async handleAssigneeSelected(id: number) {
      if (this.isSubmitting) {
        return
      }

      this.isSubmitting = true
      this.selectedPersonID = id

      try {
        let response
        if (this.isConvoRelated) {
          response = await ConversationsAPI.put_BulkEdit_Assignee({
            assigneeID: id,
            listConversationIDs: this.selectedIds,
          } as IConvosInputBulkEditAssignee)
        } else {
          response = await TasksAPI.put_BulkEdit_Assignee({
            assigneeID: id,
            listTaskIDs: this.selectedIds,
          } as ITasksInputBulkEditAssignee)
        }
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.selectedPersonID = null
      }
    },

    async handleJobAppointeeSelected(jobName: string, appointeeID: number) {
      if (this.isSubmitting) {
        return
      }

      this.isSubmitting = true
      this.selectedPersonID = appointeeID

      try {
        let response
        if (this.isConvoRelated) {
          response = await ConversationsAPI.put_BulkEdit_JobAppointee({
            appointeeID,
            jobName,
            listConversationIDs: this.selectedIds,
          } as IConvosInputBulkEditJobAppointee)
        } else {
          response = await TasksAPI.put_BulkEdit_JobAppointee({
            appointeeID,
            jobName,
            listTaskIDs: this.selectedIds,
          } as ITasksInputBulkEditJobAppointee)
        }
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.selectedPersonID = null
      }
    },

    async handlePrioritySelected(id: number) {
      if (this.isSubmitting) {
        return
      }

      this.isSubmitting = true
      this.selectedPriorityID = id

      try {
        let response
        if (this.isConvoRelated) {
          response = await ConversationsAPI.put_BulkEdit_PriorityLevel({
            priorityID: id,
            listConversationIDs: this.selectedIds,
          } as IConvosInputBulkEditPriorityLevel)
        } else {
          response = await TasksAPI.put_BulkEdit_PriorityLevel({
            priorityID: id,
            listTaskIDs: this.selectedIds,
          } as ITasksInputBulkEditPriorityLevel)
        }
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.selectedPriorityID = null
      }
    },
    async handleStatusSelected(name: string) {
      if (this.isSubmitting) {
        return
      }

      this.isSubmitting = true
      this.selectedStatusName = name

      try {
        let response
        if (this.isConvoRelated) {
          response = await ConversationsAPI.put_BulkEdit_Status({
            statusName: name,
            listConversationIDs: this.selectedIds,
          } as IConvosInputBulkEditStatus)
        } else {
          response = await TasksAPI.put_BulkEdit_Status({
            statusName: name,
            listTaskIDs: this.selectedIds,
          } as ITasksInputBulkEditStatus)
        }
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.selectedStatusName = null
      }
    },
    async updateDueDate() {
      if (this.isSubmitting) {
        return
      }
      this.isSubmitting = true

      try {
        let response
        if (this.isConvoRelated) {
          response = await ConversationsAPI.put_BulkEdit_DueDate({
            dueDate: this.selectedDueDate,
            listConversationIDs: this.selectedIds,
          } as IConvosInputBulkEditDueDate)
        } else {
          response = await TasksAPI.put_BulkEdit_DueDate({
            dueDate: this.selectedDueDate,
            listTaskIDs: this.selectedIds,
          } as ITasksInputBulkEditDueDate)
        }
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.selectedDueDate = null
      }
    },
    async removeDueDate() {
      this.selectedDueDate = null
      await this.updateDueDate()
    },
    async addTags() {
      await this.updateTags(false)
    },
    async removeTags() {
      await this.updateTags(true)
    },
    async updateTags(remove: boolean) {
      if (this.isSubmitting) {
        return
      }
      this.isSubmitting = true
      this.isTagsUpdating = true

      try {
        let response
        if (this.isConvoRelated) {
          response = await ConversationsAPI.put_BulkEdit_Tags({
            removeTags: remove,
            listTags: this.selectedTags,
            listConversationIDs: this.selectedIds,
          } as IConvosInputBulkEditTags)
        } else {
          response = await TasksAPI.put_BulkEdit_Tags({
            removeTags: remove,
            listTags: this.selectedTags,
            listTaskIDs: this.selectedIds,
          } as ITasksInputBulkEditTags)
        }
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.isTagsUpdating = false
        this.clearSelectedTags()
      }
    },
    clearSelectedTags() {
      this.selectedTags = []
    },
    async fetchOptions() {
      try {
        this.isLoadingOptions = true
        const response = this.isConvoRelated
          ? await ConversationsAPI.get_BulkEditOptions(
              TextAssistant.convertNumberArrayToStringList(
                this.selectedEntityIds,
              ),
            )
          : await TasksAPI.get_BulkEditOptions(
              TextAssistant.convertNumberArrayToStringList(
                this.selectedEntityIds,
              ),
            )

        this.entityOptions = response.listEntities
        this.assigneeOptions = response.listAssignees
        this.statusOptions = response.listStatuses
        this.priorityOptions = response.listPriorities
        this.tagOptions = response.listTags.map(t => t.name)
        this.jobOptions = response.listJobs
        this.allSelectedEntitiesUseAssignees =
          response.allSelectedEntitiesUseAssignees

        this.isLoadingOptions = false
      } catch (error) {
        this.$error.emit(error)
      }
    },
  },
})
</script>

<style lang="scss">
.bulk-edit-panel {
  $panel-padding: 16px;
  $panel-border-radius: 24px;
  $main-control-padding: 10px;

  @keyframes picture-background {
    0% {
      top: 134px;
    }
    100% {
      top: 142px;
    }
  }

  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: var(--color-background-header);
  border: $border--solid-light;
  border-radius: $panel-border-radius;
  padding: 0 $panel-padding;
  @include standard-box-shadow;
  z-index: 1;

  animation-name: picture-background;
  animation-duration: 0.15s;
  animation-fill-mode: forwards;

  &__main-control {
    display: flex;
    align-items: center;
    justify-content: center;

    height: 48px;
    border-radius: $panel-border-radius;
    @include flex-row-horizontal-spacing($main-control-padding);

    .loading-placeholder {
      width: 400px;
      justify-content: center;

      .vue-content-placeholders-text__line {
        margin-top: 10px;
        width: 100%;
      }
    }

    .close-btn {
      position: absolute;
      top: -10px;
      right: -15px;
      font-size: 12px;
      @include clickable-gray-color-states;
    }

    &--title {
      @include lettering--p-sm;
      font-weight: $bold;
      flex: 1;
      white-space: nowrap;

      .ddi-edit {
        color: var(--color-text-primary);
        padding-right: 2px;
      }
    }

    &--insta-clicks {
      display: flex;
      align-items: center;
      @include flex-row-horizontal-spacing(8px);
      border-left: $border--solid-medium;
      border-right: $border--solid-medium;
      padding-left: $main-control-padding;
      padding-right: $main-control-padding;
    }

    &--update-options {
      display: flex;
      align-items: center;
      @include flex-row-horizontal-spacing(10px);
      @include lettering--p-sm;
      border-radius: 8px;

      .jobs-dropdown {
        &__label {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: -1 * $dropdown-item-li-vertical-padding -1 * $dropdown-item-li-horizontal-padding;
          padding: $dropdown-item-li-vertical-padding
            $dropdown-item-li-horizontal-padding;
          border-radius: 4px;

          span {
            @include single-line-ellipsis;
          }
          &.selected {
            background-color: var(--color-background-blue-15);
          }
        }
        /* The popper with the job names */
        > .dropdown__popper {
          max-width: 250px;
          min-width: 250px;
          /* Need overflow: visible to allow appointees dropdown to appear since it's inside this jobs dropdown container.
          Also overriding max-height so jobs dropdown expands as needed (otherwise jobs will overflow if there's enough
          to go beyond the default max-height set in dropdown.vue) */
          overflow: visible;
          max-height: 100vh;
          .dropdown {
            width: 100%;
          }
          /* The second popper with the potential job appointees */
          .dropdown__popper {
            max-height: 300px;
          }
        }
      }

      .tags-dropdown {
        .dropdown__popper {
          max-width: 100%;
          overflow: visible;
        }

        &__content {
          display: flex;

          .tags-input {
            min-width: 340px;
            max-width: 340px;
            margin-right: 20px;
          }

          > .buttons {
            margin-top: 10px;
            display: flex;
            justify-content: flex-end;
            @include flex-row-horizontal-spacing(4px);
          }
        }
      }

      .comment-dropdown {
        .dropdown__popper {
          width: 500px;
          max-width: 500px;
          height: 400px;
        }
      }

      .ddi-warning {
        color: $color--orange-40 !important;
      }
    }
  }
}
</style>
